// bm-design-system: radio primitive
import * as React from "react";
import { cn } from "@/lib/utils";

export interface RadioProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {}

const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  ({ className, ...props }, ref) => {
    return (
      <input
        ref={ref}
        type="radio"
        className={cn(
          "h-4 w-4 shrink-0 rounded-full border border-hairline bg-page text-accent",
          "accent-[var(--color-accent)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-page",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      />
    );
  },
);
Radio.displayName = "Radio";

export interface RadioOptionProps extends Omit<RadioProps, "children"> {
  /** The option's own words. Clicking them selects the option. */
  label: React.ReactNode;
  /** Helper text beneath the label; also part of the clickable area. */
  description?: React.ReactNode;
  /**
   * Controls that belong to this option but must not select it on click —
   * a Select, a trailing phrase. Rendered beside the label, outside it, so
   * their onChange should select the option explicitly.
   */
  children?: React.ReactNode;
}

/**
 * A radio with its words, clickable on the circle and the words and nowhere
 * else.
 *
 * The label is inline-flex, so it is only as wide as its content. The row
 * around it is a plain div: blank space to the right of an option is not part
 * of any label and does nothing when clicked. Never wrap a <Radio> in a
 * full-width flex <label> — that is the fault this exists to prevent.
 */
const RadioOption = React.forwardRef<HTMLInputElement, RadioOptionProps>(
  ({ className, label, description, children, ...props }, ref) => (
    <div className={cn("flex flex-wrap items-center gap-x-2 gap-y-1", className)}>
      <label className="inline-flex items-start gap-2 text-sm font-normal text-ink-body">
        <Radio ref={ref} className="mt-0.5" {...props} />
        <span>
          {label}
          {description && (
            <span className="block text-xs text-ink-muted">{description}</span>
          )}
        </span>
      </label>
      {children}
    </div>
  ),
);
RadioOption.displayName = "RadioOption";

interface RadioGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: "vertical" | "horizontal";
}

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ className, orientation = "vertical", ...props }, ref) => (
    <div
      ref={ref}
      role="radiogroup"
      className={cn(
        "flex gap-3",
        orientation === "vertical" ? "flex-col" : "flex-row flex-wrap",
        className,
      )}
      {...props}
    />
  ),
);
RadioGroup.displayName = "RadioGroup";

export { Radio, RadioGroup, RadioOption };
